/**
 * Staff Permissions Constants
 * 
 * Mirror of backend staff permissions enum for forum moderation
 */

// Support Permissions
export const SUPPORT_VIEW_TICKETS = 'support:view_tickets'
export const SUPPORT_REPLY_TICKETS = 'support:reply_tickets'
export const SUPPORT_CLOSE_TICKETS = 'support:close_tickets'
export const SUPPORT_REOPEN_TICKETS = 'support:reopen_tickets'
export const SUPPORT_ASSIGN_TICKETS = 'support:assign_tickets'
export const SUPPORT_DELETE_TICKETS = 'support:delete_tickets'
export const SUPPORT_MANAGE_CATEGORIES = 'support:manage_categories'

// Forum Permissions
export const FORUM_VIEW_STAFF_TOOLS = 'forum:view_staff_tools'
export const FORUM_LOCK_TOPICS = 'forum:lock_topics'
export const FORUM_UNLOCK_TOPICS = 'forum:unlock_topics'
export const FORUM_PIN_TOPICS = 'forum:pin_topics'
export const FORUM_UNPIN_TOPICS = 'forum:unpin_topics'
export const FORUM_MOVE_TOPICS = 'forum:move_topics'
export const FORUM_DELETE_TOPICS = 'forum:delete_topics'
export const FORUM_DELETE_POSTS = 'forum:delete_posts'
export const FORUM_EDIT_POSTS = 'forum:edit_posts'
export const FORUM_BAN_USERS = 'forum:ban_users'
export const FORUM_UNBAN_USERS = 'forum:unban_users'
export const FORUM_MANAGE_BADGES = 'forum:manage_badges'
export const FORUM_VIEW_MODERATION_LOG = 'forum:view_moderation_log'

export type StaffPermission =
  | typeof SUPPORT_VIEW_TICKETS
  | typeof SUPPORT_REPLY_TICKETS
  | typeof SUPPORT_CLOSE_TICKETS
  | typeof SUPPORT_REOPEN_TICKETS
  | typeof SUPPORT_ASSIGN_TICKETS
  | typeof SUPPORT_DELETE_TICKETS
  | typeof SUPPORT_MANAGE_CATEGORIES
  | typeof FORUM_VIEW_STAFF_TOOLS
  | typeof FORUM_LOCK_TOPICS
  | typeof FORUM_UNLOCK_TOPICS
  | typeof FORUM_PIN_TOPICS
  | typeof FORUM_UNPIN_TOPICS
  | typeof FORUM_MOVE_TOPICS
  | typeof FORUM_DELETE_TOPICS
  | typeof FORUM_DELETE_POSTS
  | typeof FORUM_EDIT_POSTS
  | typeof FORUM_BAN_USERS
  | typeof FORUM_UNBAN_USERS
  | typeof FORUM_MANAGE_BADGES
  | typeof FORUM_VIEW_MODERATION_LOG

export const PERMISSION_GROUPS = [
  {
    label: 'Suporte',
    permissions: [
      { value: SUPPORT_VIEW_TICKETS, label: 'Ver Tickets', description: 'Visualizar todos os tickets' },
      { value: SUPPORT_REPLY_TICKETS, label: 'Responder Tickets', description: 'Responder tickets de jogadores' },
      { value: SUPPORT_CLOSE_TICKETS, label: 'Fechar Tickets', description: 'Marcar tickets como resolvidos' },
      { value: SUPPORT_REOPEN_TICKETS, label: 'Reabrir Tickets', description: 'Reabrir tickets fechados' },
      { value: SUPPORT_ASSIGN_TICKETS, label: 'Atribuir Tickets', description: 'Atribuir tickets para outros membros' },
      { value: SUPPORT_DELETE_TICKETS, label: 'Deletar Tickets', description: 'Excluir tickets permanentemente' },
      { value: SUPPORT_MANAGE_CATEGORIES, label: 'Gerenciar Categorias', description: 'Criar e editar categorias de suporte' },
    ],
  },
  {
    label: 'Fórum',
    permissions: [
      { value: FORUM_VIEW_STAFF_TOOLS, label: 'Ver Ferramentas de Staff', description: 'Ver botões e opções de moderação' },
      { value: FORUM_LOCK_TOPICS, label: 'Trancar Tópicos', description: 'Impedir novas respostas em tópicos' },
      { value: FORUM_UNLOCK_TOPICS, label: 'Destrancar Tópicos', description: 'Permitir novas respostas em tópicos trancados' },
      { value: FORUM_PIN_TOPICS, label: 'Fixar Tópicos', description: 'Fixar tópicos no topo da lista' },
      { value: FORUM_UNPIN_TOPICS, label: 'Desafixar Tópicos', description: 'Remover fixação de tópicos' },
      { value: FORUM_MOVE_TOPICS, label: 'Mover Tópicos', description: 'Mover tópicos para outras boards' },
      { value: FORUM_DELETE_TOPICS, label: 'Deletar Tópicos', description: 'Excluir tópicos permanentemente' },
      { value: FORUM_DELETE_POSTS, label: 'Deletar Posts', description: 'Excluir respostas de tópicos' },
      { value: FORUM_EDIT_POSTS, label: 'Editar Posts', description: 'Editar posts de outros usuários' },
      { value: FORUM_BAN_USERS, label: 'Banir Usuários', description: 'Banir jogadores do fórum' },
      { value: FORUM_UNBAN_USERS, label: 'Desbanir Usuários', description: 'Remover banimento do fórum' },
      { value: FORUM_MANAGE_BADGES, label: 'Gerenciar Badges', description: 'Atribuir e remover badges dos jogadores' },
      { value: FORUM_VIEW_MODERATION_LOG, label: 'Ver Log de Moderação', description: 'Visualizar histórico de ações de moderação' },
    ],
  },
]

/**
 * Helper functions for permission checking
 */
export function hasPermission(permissions: string[], permission: StaffPermission): boolean {
  return permissions.includes(permission)
}

export function hasAnyPermission(permissions: string[], requiredPermissions: StaffPermission[]): boolean {
  return requiredPermissions.some(perm => permissions.includes(perm))
}

export function hasAllPermissions(permissions: string[], requiredPermissions: StaffPermission[]): boolean {
  return requiredPermissions.every(perm => permissions.includes(perm))
}
